// SIWS CUSTOM: Metal RFID Card Holder color picker — card-holder-picker.js
// 11 colors, $16/unit, no minimum. +/- stepper per color.
// Submits as single variant with color breakdown as line item properties.
// Initialized on DOMContentLoaded for all .card-holder-picker elements.

'use strict';

// swatch: CSS background string (solid hex or gradient)
// dot:    fallback dot color for summary rows (gradient colors only)
// emoji:  flag emoji string — renders as text inside swatch circle instead of fill
const SIWS_CARD_HOLDER_COLORS = [
  { id: 'purple',     name: 'Purple',     swatch: '#7B2FBE' },
  { id: 'blue',       name: 'Blue',       swatch: '#1A56DB' },
  { id: 'green',      name: 'Green',      swatch: '#057A55' },
  { id: 'red',        name: 'Red',        swatch: '#E02424' },
  { id: 'orange',     name: 'Orange',     swatch: '#FF6700' },
  { id: 'black',      name: 'Black',      swatch: '#111827' },
  { id: 'green-line', name: 'Green Line', swatch: 'linear-gradient(#6B7280 45%, #10B981 45% 55%, #6B7280 55%)', dot: '#10B981' },
  { id: 'blue-line',  name: 'Blue Line',  swatch: 'linear-gradient(#6B7280 45%, #1A56DB 45% 55%, #6B7280 55%)', dot: '#1A56DB' },
  { id: 'red-line',   name: 'Red Line',   swatch: 'linear-gradient(#6B7280 45%, #E02424 45% 55%, #6B7280 55%)', dot: '#E02424' },
  { id: 'usa',        name: 'USA',        emoji: '🇺🇸' },
  { id: 'mexico',     name: 'Mexico',     emoji: '🇲🇽' },
];

const SIWS_CARD_HOLDER_UNIT_PRICE = 16.00;

class SIWSCardHolderPicker {
  constructor(el) {
    this.el        = el;
    this.variantId = el.dataset.variantId;
    this.colorQtys = {};
    this.init();
  }

  init() {
    this._renderColorCards();
    this._bindEvents();
    this._update();
  }

  _totalQty() {
    return Object.values(this.colorQtys).reduce((s, q) => s + q, 0);
  }

  _selectedColors() {
    return SIWS_CARD_HOLDER_COLORS.filter(c => (this.colorQtys[c.id] || 0) > 0);
  }

  _increment(id) {
    this.colorQtys[id] = (this.colorQtys[id] || 0) + 1;
    this._update();
  }

  _decrement(id) {
    const current = this.colorQtys[id] || 0;
    if (current <= 0) return;
    this.colorQtys[id] = current - 1;
    this._update();
  }

  _renderColorCards() {
    const container = this.el.querySelector('#card-holder-color-cards');
    if (!container) return;

    const grid = document.createElement('div');
    // SIWS CUSTOM: Reuses screwdriver-picker.css grid/card/stepper classes — same visual pattern.
    grid.className = 'screwdriver-color-grid';

    SIWS_CARD_HOLDER_COLORS.forEach(color => {
      grid.appendChild(this._buildCardEl(color));
    });

    container.appendChild(grid);
  }

  _buildCardEl(color) {
    const card = document.createElement('div');
    card.className       = 'screwdriver-color-card';
    card.dataset.colorId = color.id;

    const swatchWrap = document.createElement('div');
    swatchWrap.className = 'screwdriver-color-card__swatch-wrap';

    const swatch = document.createElement('span');
    swatch.className = 'screwdriver-color-card__swatch';
    swatch.setAttribute('aria-hidden', 'true');

    if (color.emoji) {
      // SIWS CUSTOM: Flag colors — emoji centered inside the circle, no fill color.
      swatch.textContent          = color.emoji;
      swatch.style.display        = 'flex';
      swatch.style.alignItems     = 'center';
      swatch.style.justifyContent = 'center';
      swatch.style.fontSize       = '1.6rem';
      swatch.style.lineHeight     = '1';
    } else {
      // Solid color or CSS gradient (linear-gradient string works via background shorthand).
      swatch.style.background = color.swatch;
    }

    swatchWrap.appendChild(swatch);

    const name = document.createElement('span');
    name.className   = 'screwdriver-color-card__name';
    name.textContent = color.name;

    const stepper = document.createElement('div');
    stepper.className = 'screwdriver-color-card__stepper';

    const minusBtn = document.createElement('button');
    minusBtn.type            = 'button';
    minusBtn.className       = 'screwdriver-color-card__minus';
    minusBtn.dataset.colorId = color.id;
    minusBtn.textContent     = '−';
    minusBtn.disabled        = true;
    minusBtn.setAttribute('aria-label', `Remove one ${color.name}`);

    const qtySpan = document.createElement('span');
    qtySpan.className   = 'screwdriver-color-card__qty';
    qtySpan.textContent = '0';

    const plusBtn = document.createElement('button');
    plusBtn.type            = 'button';
    plusBtn.className       = 'screwdriver-color-card__plus';
    plusBtn.dataset.colorId = color.id;
    plusBtn.textContent     = '+';
    plusBtn.setAttribute('aria-label', `Add one ${color.name}`);

    stepper.appendChild(minusBtn);
    stepper.appendChild(qtySpan);
    stepper.appendChild(plusBtn);

    card.appendChild(swatchWrap);
    card.appendChild(name);
    card.appendChild(stepper);

    return card;
  }

  _bindEvents() {
    this.el.addEventListener('click', e => {
      const plusBtn  = e.target.closest('.screwdriver-color-card__plus');
      const minusBtn = e.target.closest('.screwdriver-color-card__minus');
      if (plusBtn)  this._increment(plusBtn.dataset.colorId);
      if (minusBtn) this._decrement(minusBtn.dataset.colorId);
    });

    const atcBtn = this.el.querySelector('#card-holder-atc-btn');
    if (atcBtn) atcBtn.addEventListener('click', () => this._addToCart());
  }

  _update() {
    this._updateCardStates();
    this._updateSummary();
    this._updateButton();
  }

  _updateCardStates() {
    this.el.querySelectorAll('.screwdriver-color-card').forEach(card => {
      const id  = card.dataset.colorId;
      const qty = this.colorQtys[id] || 0;

      card.classList.toggle('screwdriver-color-card--selected', qty > 0);
      card.classList.remove('screwdriver-color-card--warning');

      const plusBtn  = card.querySelector('.screwdriver-color-card__plus');
      const minusBtn = card.querySelector('.screwdriver-color-card__minus');
      const qtySpan  = card.querySelector('.screwdriver-color-card__qty');

      if (qtySpan)  qtySpan.textContent = qty;
      if (minusBtn) minusBtn.disabled   = qty === 0;
      if (plusBtn)  plusBtn.disabled    = false;
    });
  }

  _updateSummary() {
    const summaryEl = this.el.querySelector('#card-holder-summary');
    if (!summaryEl) return;

    const selected = this._selectedColors();
    if (selected.length === 0) {
      summaryEl.hidden = true;
      return;
    }

    const total     = this._totalQty();
    const totalCost = (total * SIWS_CARD_HOLDER_UNIT_PRICE).toFixed(2);

    const rows = selected.map(c => {
      const qty      = this.colorQtys[c.id];
      const lineCost = (qty * SIWS_CARD_HOLDER_UNIT_PRICE).toFixed(2);

      // SIWS CUSTOM: Build the summary dot — gradient colors use center color; emoji colors use emoji text.
      let dotHtml;
      if (c.emoji) {
        dotHtml = `<span style="margin-right:0.5rem;font-size:0.9rem;">${c.emoji}</span>`;
      } else {
        const dotBg = c.dot || c.swatch;
        dotHtml = `<span class="screwdriver-summary__dot" style="background:${dotBg};"></span>`;
      }

      return `<tr>
        <td>${dotHtml}${c.name}</td>
        <td class="screwdriver-summary__qty-cell">${qty} &times; $${SIWS_CARD_HOLDER_UNIT_PRICE.toFixed(2)} = $${lineCost}</td>
      </tr>`;
    }).join('');

    summaryEl.innerHTML = `
      <h4 class="screwdriver-summary__title">Adding to Cart</h4>
      <table class="screwdriver-summary__table">
        <tbody>${rows}</tbody>
        <tfoot>
          <tr class="screwdriver-summary__total-row">
            <td>Total</td>
            <td class="screwdriver-summary__qty-cell">${total} units &middot; $${totalCost}</td>
          </tr>
        </tfoot>
      </table>`;
    summaryEl.hidden = false;
  }

  _updateButton() {
    const atcBtn = this.el.querySelector('#card-holder-atc-btn');
    if (!atcBtn) return;

    const total = this._totalQty();
    const cost  = (total * SIWS_CARD_HOLDER_UNIT_PRICE).toFixed(2);

    if (total === 0) {
      atcBtn.disabled    = true;
      atcBtn.textContent = 'Select colors to continue';
    } else {
      atcBtn.disabled    = false;
      atcBtn.textContent = `Add ${total} to Cart — $${cost}`;
    }
  }

  _showError(message) {
    const el = this.el.querySelector('#card-holder-atc-error');
    if (!el) return;
    el.textContent = message;
    el.hidden      = false;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  _hideError() {
    const el = this.el.querySelector('#card-holder-atc-error');
    if (el) el.hidden = true;
  }

  async _addToCart() {
    const atcBtn = this.el.querySelector('#card-holder-atc-btn');
    if (!atcBtn || atcBtn.disabled) return;

    const selected = this._selectedColors();
    const total    = this._totalQty();

    // SIWS CUSTOM: Line item properties — one per selected color + compact breakdown string.
    const properties = { 'Total units': String(total) };
    selected.forEach(c => {
      properties[c.name] = String(this.colorQtys[c.id]);
    });
    properties['_color_breakdown'] = selected
      .map(c => `${c.name}: ${this.colorQtys[c.id]}`)
      .join(', ');

    const originalText  = atcBtn.textContent;
    atcBtn.disabled     = true;
    atcBtn.textContent  = 'Adding to cart\u2026';
    this._hideError();

    try {
      // SIWS CUSTOM (2026-05-12): augment body with Section Rendering API hints
      // so the Dawn cart drawer/notification can re-render itself from the response.
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = {
        items: [{ id: this.variantId, quantity: total, properties }],
        sections:     cart ? cart.getSectionsToRender().map((s) => s.id) : ['cart-icon-bubble'],
        sections_url: window.location.pathname,
      };

      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart.');
      }

      // SIWS CUSTOM (2026-05-12): swap redirect for Dawn cart drawer/notification.
      // Page-mode fallback preserves prior behavior when no cart element exists.
      const responseData = await res.json();
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'siws-card-holder', cartData: responseData });
      }
      if (cart) {
        cart.renderContents(responseData);
      } else {
        // SIWS CUSTOM (2026-05-12): Page-mode — stay on PDP, refresh cart bubble.
        const newBubbleHTML = responseData.sections && responseData.sections['cart-icon-bubble'];
        const liveBubble = document.getElementById('cart-icon-bubble');
        if (newBubbleHTML && liveBubble) {
          const parsed = new DOMParser().parseFromString(newBubbleHTML, 'text/html');
          const inner = parsed.querySelector('.shopify-section');
          if (inner) liveBubble.innerHTML = inner.innerHTML;
        }
        // SIWS CUSTOM (2026-05-12): clear picker state so a re-click can't double-add.
        this.colorQtys = {};
        this._update();
      }

    } catch (err) {
      console.error('[SIWS] Card holder add-to-cart error:', err);
      atcBtn.disabled    = false;
      atcBtn.textContent = originalText;
      this._showError(err.message || 'Something went wrong. Please try again.');
    }
  }
}

function _siwsInitCardHolderPickers() {
  document.querySelectorAll('.card-holder-picker').forEach(el => {
    new SIWSCardHolderPicker(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _siwsInitCardHolderPickers);
} else {
  _siwsInitCardHolderPickers();
}
